import React, { useState } from 'react';
import { PayPalScriptProvider, PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import api from './api';
import { 
  Modal, Box, Typography, CircularProgress, 
  TextField, Button, Grid, IconButton 
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close'; 

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%', 
  maxWidth: 400, 
  maxHeight: '90vh',
  overflowY: 'auto',
  display: 'block',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 3,                
};

const paypalOptions = {
  "client-id": "ATdqEdoY6_5pGQ3Cl2MFxjw8MuaH9lOMIp2_bcUAz-Zet59_LMhPiYpDv4vFC_pXVCLD7C9iLCdqGfTH",
  currency: "AUD",
  intent: "capture",
};

function PaymentModalContent({ onClose, display, onPaymentSuccess }) {
  // --- AUTOMATIC YEAR VARIABLES ---
  const currentYear = new Date().getFullYear();
  const promoCodeHint = `XMAS${String(currentYear).slice(-2)}`; // e.g. XMAS26
  // --------------------------------

  const [{ isPending }] = usePayPalScriptReducer();
  const [error, setError] = useState('');
  
  const [code, setCode] = useState(''); 
  const [discount, setDiscount] = useState(0); 
  const [codeMessage, setCodeMessage] = useState(''); 
  const [finalAmount, setFinalAmount] = useState("2.00"); 

  const handleApplyCode = async () => {
    try {
      const response = await api.post('/apply-code', { code: code });
      const discountAmount = parseFloat(response.data.discountAmount);
      
      setDiscount(discountAmount); 

      const newTotal = (2.00 - discountAmount).toFixed(2);
      setFinalAmount(newTotal);
      setCodeMessage(response.data.message);
      setError('');
    } catch (err) {
      setCodeMessage(err.response?.data?.message || 'Code not found');
      setDiscount(0);
      setFinalAmount("2.00");
    }
  };

  const createOrder = (data, actions) => {
    return actions.order.create({
      purchase_units: [
        {
          reference_id: display.id.toString(),
          
          // Main Description (Visible in Summary)
          description: `Listing: ${display.address.substring(0, 100)}`,
          
          // Custom ID for Backend Reports
          custom_id: `DISPLAY-${display.id}`,
          
          amount: {
            value: finalAmount,
            currency_code: "AUD",
            breakdown: {
              item_total: {
                value: finalAmount,
                currency_code: "AUD"
              }
            }
          },

          // --- UPDATED ITEM DETAILS ---
          items: [
            {
              // Dynamically sets the year in the PayPal receipt
              name: `Christmas Lights Map Listing ${currentYear} - ${display.address.substring(0, 60)}`, 
              
              // Description: Full address
              description: display.address.substring(0, 120),
              
              // SKU: "ID:105 - 123 Example St"
              sku: `ID:${display.id} - ${display.address.substring(0, 50)}`,
              
              unit_amount: {
                value: finalAmount,
                currency_code: "AUD"
              },
              quantity: "1",
              category: "DIGITAL_GOODS"
            }
          ]
          // ----------------------------
        },
      ],
    });
  };

  const onApprove = async (data, actions) => {
    try {
      const response = await api.post('/payment/verify', { 
        orderID: data.orderID,
        displayID: display.id,
        discountCode: discount > 0 ? code : null
      });
      onPaymentSuccess(display.id, response.data); 
      onClose();
    } catch (err) {
      setError('Payment verification failed.');
    }
  };

  const onError = (err) => {
    console.error("PayPal Error:", err);
    setError("An error occurred with the PayPal payment.");
  };

  const handleFreeOrder = async () => {
    try {
      const response = await api.post('/payment/verify', { displayID: display.id, discountCode: code });
      onPaymentSuccess(display.id, response.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Free order verification failed.');
    }
  };

  return (
    <Box sx={style}>
        <IconButton 
          onClick={onClose} 
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h6" component="h2" gutterBottom align="center" sx={{ mt: 2 }}>
          Complete Submission
        </Typography>
        <Typography sx={{ mb: 2, textAlign: 'center', fontSize: '0.9rem' }}>
          {display.address}
        </Typography>

        <Grid 
          container 
          spacing={1} 
          sx={{ 
            mb: 2, 
            alignItems: 'center', 
            width: '100%',
            maxWidth: '320px', 
            mx: 'auto' 
          }} 
        >
          <Grid item xs={8}>
            <TextField
              label="Discount Code"
              size="small"
              fullWidth
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <Button variant="contained" onClick={handleApplyCode} size="small" fullWidth>
              Apply
            </Button>
          </Grid>
        </Grid>

        {codeMessage && <Typography sx={{ mb: 1, width: '100%', fontSize: '0.8rem', textAlign: 'center' }}>{codeMessage}</Typography>}

        <Typography variant="caption" sx={{ mb: 2, fontStyle: 'italic', width: '100%', display: 'block', textAlign: 'center' }}>
          Use code <span style={{fontWeight: 'bold'}}>{promoCodeHint}</span> Hurry while discounts last!
        </Typography>

        <Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
          Total: ${finalAmount}
        </Typography>
        
        <Box sx={{ width: '100%', minHeight: '150px' }}>
          {finalAmount === "0.00" ? (
            <Button variant="contained" color="primary" fullWidth onClick={handleFreeOrder}>
              Confirm Free Order
            </Button>
          ) : isPending ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
          ) : (
            <PayPalButtons
              key={finalAmount} 
              style={{ layout: "vertical" }}
              createOrder={createOrder}
              onApprove={onApprove}
              onError={onError}
            />
          )}
          {error && <Typography color="error" sx={{ mt: 2, textAlign: 'center' }}>{error}</Typography>}
        </Box>
        
        <Button onClick={onClose} sx={{ mt: 2 }} fullWidth>Cancel</Button>
    </Box>
  );
}

export default function PaymentModal(props) {
  if (!props.open) return null;

  return (
    <Modal open={props.open} onClose={props.onClose}>
      <React.Fragment> 
        <PayPalScriptProvider options={paypalOptions}>
           <PaymentModalContent {...props} />
        </PayPalScriptProvider>
      </React.Fragment>
    </Modal>
  );
}